import React from 'react';
import { Box, Container, Typography, Button, Paper } from '@mui/material';

const CompleteDsaCoursePage: React.FC = () => {
  return (
    <Box sx={{ bgcolor: 'background.default', minHeight: '100vh', py: 8 }}>
      <Container maxWidth="md">
        {/* Hero Section */}
        <Box sx={{ textAlign: 'center', mb: 8 }}>
          <Typography variant="h2" sx={{ fontWeight: 800, mb: 3, color: 'text.primary' }}>
            Master LeetCode Like a Top 2% Engineer
          </Typography>
          <Typography variant="h6" sx={{ color: 'text.secondary', mb: 4 }}>
            For aspiring FAANG engineers who know they need to grind but keep spinning their wheels
          </Typography>

          <Typography variant="h4" sx={{ fontWeight: 700, color: 'secondary.main', mb: 3 }}>
            Get top 2% without wasting years like I did.
          </Typography>

          <Typography variant="body1" sx={{ mb: 5, lineHeight: 1.8, fontSize: '1.1rem' }}>
            Get instant access to the exact 477 problems that took me to top 2% (solved in optimal order), 100+ hours of live problem-solving sessions showing my actual thought process, the beginner foundations that saved my CS degree, the intermediate set that landed my Citibank internship and conversion, plus my personal improvement framework that finally made everything click.
          </Typography>

          <Button
            variant="contained"
            size="large"
            sx={{
              py: 2,
              px: 6,
              fontSize: '1.1rem',
              bgcolor: 'secondary.main',
              color: 'black',
              '&:hover': { bgcolor: 'secondary.dark' },
              mb: 2
            }}
          >
            Get Access for $11
          </Button>
          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
            Or join the $8/month community for free access
          </Typography>
        </Box>

        {/* Pain Points */}
        <Paper elevation={3} sx={{ p: 5, mb: 8, bgcolor: 'background.paper' }}>
          <Typography variant="h4" sx={{ fontWeight: 700, mb: 4 }}>
            If any of this sounds like you...
          </Typography>

          {[
            "You know you need to grind LeetCode but you keep solving random problems with no clear path forward.",
            "You've been \"studying\" for months but your profile shows 47 problems solved and you can't explain why you're not making progress.",
            "You buy theory books and watch courses but when you open a medium problem you still freeze and don't know where to start.",
            "You solve easy problems over and over because they feel productive but you're not actually getting better at the hard stuff that shows up in real interviews.",
            "You failed an interview at a company you really wanted and realized you've been preparing completely wrong this entire time.",
            "You're watching other people get Google and Meta offers while you're stuck in the same place you were six months ago.",
            "You have no idea if you're doing enough volume, if you're picking the right problems, or if you're even improving at all."
          ].map((point, index) => (
            <Typography
              key={index}
              variant="body1"
              sx={{
                py: 2,
                borderBottom: index < 6 ? '1px solid rgba(255, 215, 0, 0.1)' : 'none',
                lineHeight: 1.7
              }}
            >
              {point}
            </Typography>
          ))}
        </Paper>

        {/* Benefits */}
        <Paper elevation={3} sx={{ p: 5, mb: 8, bgcolor: 'background.paper' }}>
          <Typography variant="h4" sx={{ fontWeight: 700, mb: 4 }}>
            What happens when you finally have the right roadmap
          </Typography>

          {[
            "Instead of wandering through 3,000 random problems, you follow the exact 477 that took someone from struggling CS student to top 2% and a Walmart offer.",
            "You stop lying to yourself about your progress because you can see exactly what real preparation looks like—111 problems in month one, 105 in month two—and you finally understand the volume that actually works.",
            "You skip the 6 years of mistakes I made (reading theory books, only doing easy problems, following ten different roadmaps) and compress my entire learning journey into a focused 2-3 month sprint.",
            "You watch me solve these exact problems live and see how a top 2% engineer actually thinks through the hard moments, so you're not just reading solutions but absorbing the problem-solving patterns that matter.",
            "You start with problems so fundamental they rebuild your foundations (even if you \"know\" the basics), then progress through the intermediate set that gets you hired, then tackle the advanced problems that get you into FAANG.",
            "Your LeetCode profile transforms from embarrassing to undeniable—consistent daily streaks, climbing difficulty, documented proof that you're not just talking about grinding but actually doing it.",
            "You stop feeling lost and start feeling prepared because you know exactly what problem to solve next and exactly why it matters for your progression."
          ].map((benefit, index) => (
            <Typography
              key={index}
              variant="body1"
              sx={{
                py: 2,
                borderBottom: index < 6 ? '1px solid rgba(255, 215, 0, 0.1)' : 'none',
                lineHeight: 1.7
              }}
            >
              {benefit}
            </Typography>
          ))}
        </Paper>

        {/* Solution Intro */}
        <Paper
          elevation={3}
          sx={{
            p: 6,
            mb: 8,
            textAlign: 'center',
            bgcolor: 'secondary.main',
            color: 'black'
          }}
        >
          <Typography variant="h3" sx={{ fontWeight: 700, mb: 3, color: 'black' }}>
            Introducing: The Ray Roadmap
          </Typography>
          <Typography variant="h6" sx={{ lineHeight: 1.8, color: 'black' }}>
            The exact problem sequence that took me from failing college to top 2%. This isn't another generic "Top 150 LeetCode Problems" list copied from Reddit. This is my actual documented journey from 2018 to 2024, the real problems I solved in the real order that finally worked, after wasting years on approaches that didn't.
          </Typography>
        </Paper>

        {/* Story */}
        <Paper
          elevation={3}
          sx={{
            p: 5,
            mb: 8,
            bgcolor: 'background.paper',
            borderLeft: '4px solid',
            borderColor: 'secondary.main'
          }}
        >
          <Typography variant="h4" sx={{ fontWeight: 700, mb: 4 }}>
            How I discovered what actually works (after 6 years of doing it wrong)
          </Typography>

          {[
            "I started in 2018 thinking I was smart enough to just read algorithms textbooks. I was even a TA for data structures—I could draw Dijkstra's algorithm on a whiteboard and explain Big O notation. But when I sat down for my first real interview, I completely choked. Theory didn't translate to actually solving problems under pressure.",
            "So I tried grinding LeetCode but made another mistake: I only solved easy problems. My 2018 profile is embarrassing—one submission here, one submission there. I told myself I was \"preparing seriously\" but the data doesn't lie. I was stuck at the same level for years because I never forced myself into the struggle zone.",
            "Then I failed my Google interview in 2021. Barely failed. And that's when I finally got serious. I looked at my profile and realized: this isn't about reading more or understanding more theory. It's about volume and progressive difficulty. I needed to look like someone actually training, not someone pretending to train.",
            "In 2024, I solved 111 problems in one month. Then 105 the next month. I stopped reading books and just solved problems from a single focused roadmap. I did spaced repetition—going back to problems I couldn't solve. I tracked weak topics and deliberately practiced them. And I documented everything live because I knew future me (or someone like you) would need to see the real process, not the highlight reel.",
            "The breakthrough wasn't a secret technique. It was realizing that LeetCode is exactly like the gym: you can't just keep lifting the same weight and expect to get stronger. You need progressive overload. You need consistency. You need to stop lying to yourself about how hard you're actually working.",
            "This roadmap is the productization of that realization. It's not my opinion on what you should study—it's my actual tracked progression from someone who couldn't solve easy problems to someone in the top 2% globally. Every problem in here is battle-tested. Every problem moved the needle. And I can prove it because my entire journey is public."
          ].map((paragraph, index) => (
            <Typography key={index} variant="body1" sx={{ mb: 3, lineHeight: 1.8 }}>
              {paragraph}
            </Typography>
          ))}
        </Paper>

        {/* Offer Stack */}
        <Box sx={{ mb: 8 }}>
          <Typography variant="h4" sx={{ fontWeight: 700, mb: 4 }}>
            What's included in The Ray Roadmap
          </Typography>

          {[
            {
              title: "The Ray 50 (Beginner Foundation Set)",
              description: "Problems so simple you might think they're insulting, but these are exactly what I wish I had in 2016 when I was failing my CS classes. \"Check if a number exists in a list\" level fundamental. If these are hard for you, you know exactly where to start. If they're easy, you'll fly through them in a day and build unstoppable momentum."
            },
            {
              title: "The Ray 169 (Intermediate Employment Set)",
              description: "These are the problems that got me my Citibank internship and full-time conversion. Curated from the famous Grind 169 list but reordered based on my actual solving experience. This is the skill level that gets you hired at good companies that aren't FAANG—the tier where most people actually end up working."
            },
            {
              title: "The Ray 477 (Advanced Top 2% Set)",
              description: "My complete 2024 progression that took me into the top 2% globally on LeetCode. These are randomly sorted from NeetCode's Zero to Track, which means you're constantly encountering new patterns and can't game the system. Solve these and you'll be ready for Google, Meta, Netflix—the companies where the real money is."
            },
            {
              title: "100+ Hours of Live Problem-Solving Archives",
              description: "Not clean tutorial videos. Raw footage of me actually solving these problems in real-time, including the parts where I get stuck, the parts where I realize my approach is wrong, the parts where I have to refactor. This is the behind-the-scenes that nobody else shows you because it's not glamorous. But it's where the real learning happens."
            },
            {
              title: "My Personal Improvement Framework",
              description: "The spaced repetition approach, weak topic identification system, and volume tracking method that finally made everything click after years of spinning my wheels. This isn't motivational fluff—it's the operational system that transformed my profile from \"one submission per week\" to \"111 problems per month.\""
            },
            {
              title: "Complete Public Accountability",
              description: "Every problem I solved is timestamped on my public LeetCode profile. You can verify everything I'm claiming. You can see my 2018 profile where I was terrible. You can see my 2024 profile where I finally figured it out. No hiding, no exaggeration, just documented proof that this approach works."
            }
          ].map((item, index) => (
            <Paper
              key={index}
              elevation={2}
              sx={{
                p: 4,
                mb: 3,
                bgcolor: 'background.paper',
                border: '2px solid',
                borderColor: 'rgba(255, 215, 0, 0.1)',
                transition: 'all 0.3s',
                '&:hover': {
                  borderColor: 'secondary.main',
                  boxShadow: '0 4px 12px rgba(255, 215, 0, 0.2)'
                }
              }}
            >
              <Typography variant="h6" sx={{ color: 'secondary.main', mb: 2, fontWeight: 600 }}>
                {item.title}
              </Typography>
              <Typography variant="body1" sx={{ lineHeight: 1.7, color: 'text.secondary' }}>
                {item.description}
              </Typography>
            </Paper>
          ))}
        </Box>

        {/* FAQ Section */}
        <Box sx={{ mb: 8 }}>
          <Typography variant="h4" sx={{ fontWeight: 700, mb: 5 }}>
            Frequently Asked Questions
          </Typography>

          {[
            {
              question: "Why should I trust your roadmap over others?",
              answer: "Because mine has receipts. My entire LeetCode journey from 2018 to 2024 is publicly documented. You can see the exact dates I solved each problem. You can see my months of failure and my months of success. Most roadmaps are just someone's opinion on what you should study. Mine is a proven path because I actually walked it and you can verify every step."
            },
            {
              question: "I'm a complete beginner who struggles with basic coding. Will this work for me?",
              answer: "Yes—start with The Ray 50. These are embarrassingly simple problems like \"check if a number is in a list.\" If you're a new grad and these are hard, you probably cheated through your classes (harsh but true). If you're a first-year student, these are exactly the foundations you need. Don't skip them. They build the muscle memory that makes everything else possible."
            },
            {
              question: "I've already solved 200+ problems but I'm still failing interviews. How is this different?",
              answer: "You probably did what I did for years: random problem selection with no progressive difficulty. Or you kept doing easy/medium problems and never forced yourself into the genuinely hard stuff. This roadmap is ordered based on actual progression—you can't cheat by staying in your comfort zone. And the live solving videos show you the thinking process, not just the solution."
            },
            {
              question: "How long will this take me?",
              answer: "If you match my 2024 volume (roughly 3 problems per day), you can do The Ray 477 in about 2-3 months. That's all it took me to go from failing my Google interview to top 2%. You don't need years—you need focused intensity. If you're intermediate and just want employment, The Ray 169 can be done in a month. Be honest with yourself about your volume."
            },
            {
              question: "Do I need to do all 477 problems or can I just cherry-pick?",
              answer: "You can cherry-pick, but you're robbing yourself. The power is in the sequence and the volume. These problems are ordered based on my actual learning progression—they build on each other. Skipping around is exactly what I did for years and it kept me stuck. If you want different results, you need to do things differently."
            },
            {
              question: "What if I get stuck on a problem?",
              answer: "Watch my live solving video for that exact problem. You'll see how I approach it, where I get stuck, and how I work through it. Then try again yourself. If you still can't solve it, mark it for spaced repetition and come back in a week. Don't just read the solution—that's the theory trap that wasted years of my time."
            },
            {
              question: "Is this just for FAANG or will it help me get any software job?",
              answer: "The Ray 169 (intermediate set) is specifically what got me my Citibank internship and conversion—that's a good but not elite tech job. If you just want employment at a normal company, you don't even need The Ray 477. But if you want $300K-500K FAANG money, you need to go all the way. Your choice."
            },
            {
              question: "Why is it only $11? That seems too cheap for 477 problems and 100+ hours of video.",
              answer: "Because I'm not trying to sell you a $997 course with false scarcity. I'm productizing my public LeetCode journey. The problems are free on LeetCode—I'm just giving you the order and my solving approach. I want this to be accessible to broke college students who need it most. If you're already in the $8 community, it's completely free."
            },
            {
              question: "Can I access this forever or is it a subscription?",
              answer: "One-time payment of $11 gets you lifetime access to everything. The roadmap, all the videos, all future updates as I continue solving problems. Or join the $8/month community and get it included—but even if you cancel the community later, you keep access to this roadmap."
            },
            {
              question: "What's the refund policy?",
              answer: "If you get through The Ray 50 and Ray 169 with genuine effort (actually solving them, not just reading solutions) and you don't feel more prepared than when you started, I'll refund you. But you have to actually do the work—I can check your LeetCode profile. No refunds for people who buy it and don't use it."
            },
            {
              question: "I already have a job. Why would I do this?",
              answer: "Because you're probably making $80K-120K and you could be making $300K-500K at FAANG. That's the trade-off. You don't need LeetCode skills for most software jobs—they're useless in daily work. But they're the gate to the highest paying positions. If that's not worth 2-3 months of focused grinding to you, don't do it. But be honest about what you're leaving on the table."
            },
            {
              question: "Last question: What happens if I don't buy this?",
              answer: "Nothing. You can keep doing what you're doing. But in 6 months, will your LeetCode profile look different than it does today? Or will you be exactly where you are now, still telling yourself you're \"preparing\" while your profile shows 53 problems solved? The roadmap isn't magic—it's just forcing you to confront reality and stop lying to yourself about your effort level."
            }
          ].map((faq, index) => (
            <Box key={index} sx={{ mb: 5 }}>
              <Typography variant="h6" sx={{ fontWeight: 600, mb: 1.5 }}>
                {faq.question}
              </Typography>
              <Typography variant="body1" sx={{ lineHeight: 1.8, color: 'text.secondary' }}>
                {faq.answer}
              </Typography>
            </Box>
          ))}
        </Box>

        {/* Final CTA */}
        <Paper elevation={3} sx={{ p: 8, textAlign: 'center', bgcolor: 'background.paper' }}>
          <Typography variant="h4" sx={{ fontWeight: 700, mb: 3 }}>
            Ready to stop spinning your wheels?
          </Typography>
          <Button
            variant="contained"
            size="large"
            sx={{
              py: 2,
              px: 6,
              fontSize: '1.1rem',
              bgcolor: 'secondary.main',
              color: 'black',
              '&:hover': { bgcolor: 'secondary.dark' },
              mb: 2
            }}
          >
            Get The Ray Roadmap for $11
          </Button>
          <Typography variant="body2" sx={{ color: 'text.secondary', mb: 1 }}>
            Or join the $8/month community for free access + coaching
          </Typography>
          <Typography variant="caption" sx={{ color: 'text.secondary' }}>
            Lifetime access. No subscription. Same problems that got me to top 2% and a Walmart offer.
          </Typography>
        </Paper>
      </Container>
    </Box>
  );
};

export default CompleteDsaCoursePage;
